// ─── FuelOrderWizard ────────────────────────────────────────────────
// Wizard dedicado para criar OTs de Distribuição de Combustível
// (Move Express). Substitui o OrderForm genérico no caso fuel.
//
// Linguagem directa, dropdowns fixos, geocoding multi-provider via
// AddressFieldShared (igual ao OrderForm). Não toca no OrderForm.js.
//
// Pedido composto: quando o toggle "Pedido composto" é ligado, abre uma 2ª coluna ao
// lado para preencher a OT B em simultâneo. No submit cria as 2 OTs
// e liga-as via linkedToOtIds (POST→POST→PUT).
import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Box, Typography,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel, Collapse,
  Menu, Chip, CircularProgress, Tooltip, InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import StarIcon from '@material-ui/icons/Star';
import RoomIcon from '@material-ui/icons/Room';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import BusinessIcon from '@material-ui/icons/Business';
import EventIcon from '@material-ui/icons/Event';
import NotesIcon from '@material-ui/icons/Notes';
import { useDispatch } from 'react-redux';
import { createOrder, updateOrder } from '../../store/slices/transportOrdersSlice';
import { AuthContext } from '../../App';
import AddressFieldShared from './AddressFieldShared';
import { recomputeRouteAfterOTChange } from '../../utils/routeSync';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: 14,
    overflow: 'hidden',
  },
  // Cabeçalho — gradiente soft no estilo do painel KPI da Frota (FleetView)
  title: {
    background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 60%)',
    color: '#9a3412',
    padding: '14px 22px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: '1px solid #fed7aa',
  },
  titleLeft: {
    display: 'flex', alignItems: 'center', gap: 12,
  },
  titleIconBox: {
    width: 34, height: 34, borderRadius: 9,
    background: '#fff',
    border: '1px solid #fed7aa',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#ea580c',
  },
  titleText: {
    fontSize: '1.0rem', fontWeight: 800,
    letterSpacing: '-0.005em',
    color: '#9a3412',
  },
  titleSub: {
    fontSize: '0.6rem', fontWeight: 800,
    color: '#c2410c',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 2,
  },

  content: {
    padding: '18px 22px',
    background: '#f8fafc',
  },

  // Linha do topo — painel KPI envolvente (estilo das cisternas no FleetView)
  topRowWrap: {
    margin: '0 0 16px 0', padding: '10px 12px', borderRadius: 12,
    background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 60%)',
    border: '1px solid #fed7aa',
    display: 'flex', alignItems: 'stretch', gap: 10, flexWrap: 'wrap',
  },
  topCard: {
    background: '#fff',
    border: '1.5px solid rgba(249,115,22,0.20)',
    borderRadius: 10,
    padding: '6px 14px',
    display: 'flex', flexDirection: 'column', gap: 3,
    minWidth: 140, flex: 1,
    transition: 'all 0.12s ease',
  },
  topCardActive: {
    background: '#f97316',
    border: '1.5px solid #f97316',
    boxShadow: '0 2px 8px rgba(249,115,22,0.35)',
  },
  topLabel: {
    fontSize: '0.6rem', fontWeight: 800,
    color: '#9a3412', textTransform: 'uppercase',
    letterSpacing: 0.5,
    display: 'flex', alignItems: 'center', gap: 4,
  },
  topLabelActive: { color: '#fff' },
  topValue: {
    fontSize: '1.0rem', fontWeight: 800,
    color: '#0f172a',
    fontFamily: 'ui-monospace, Menlo, monospace',
    lineHeight: 1.15,
  },
  topValueActive: { color: '#fff' },

  // Cards
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 12,
    boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
  },
  cardFuel: {
    background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 60%)',
    border: '1px solid #fed7aa',
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 12,
  },
  cardHeaderFuel: {},
  cardTitle: {
    fontSize: '0.66rem', fontWeight: 800, color: '#475569',
    textTransform: 'uppercase', letterSpacing: 0.6,
    flex: 1,
  },
  cardTitleFuel: { color: '#9a3412' },
  cardTitleHint: {
    fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.4,
  },
  iconCircle: {
    width: 26, height: 26, borderRadius: 7,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#fff7ed', color: '#ea580c',
    border: '1px solid #fed7aa',
  },
  iconCircleNeutral: {
    background: '#eff6ff', color: '#1e40af',
    border: '1px solid #bfdbfe',
  },
  iconCircleGreen: {
    background: '#dcfce7', color: '#15803d',
    border: '1px solid #bbf7d0',
  },

  // Grelhas
  row3: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1fr',
    gap: 12,
    alignItems: 'flex-start',
  },
  row2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },

  // Volume / litros — mini-card KPI (estável, não muda layout ao "ajustar")
  litresBox: {
    background: '#fff',
    border: '1.5px solid #fed7aa',
    borderRadius: 10,
    padding: '6px 12px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 6,
    minHeight: 56,
    transition: 'all 0.12s ease',
  },
  litresLabel: {
    fontSize: '0.6rem', fontWeight: 800,
    color: '#9a3412', textTransform: 'uppercase',
    letterSpacing: 0.5,
    display: 'block',
  },
  litresValue: {
    fontSize: '1.0rem', fontWeight: 800,
    color: '#0f172a',
    fontFamily: 'ui-monospace, Menlo, monospace',
    lineHeight: 1.15,
  },
  litresEditBtn: {
    minWidth: 0, padding: '3px 8px',
    fontSize: '0.62rem',
    color: '#9a3412', background: '#fff7ed',
    border: '1px solid #fdba74', borderRadius: 6,
    textTransform: 'uppercase', fontWeight: 800,
    letterSpacing: 0.4,
    '&:hover': { background: '#ffedd5' },
  },
  litresInputWrap: {
    background: '#fff', border: '1.5px solid #f97316',
    borderRadius: 10,
    padding: '4px 10px',
    display: 'flex', alignItems: 'center', gap: 4,
    minHeight: 56,
    boxShadow: '0 2px 6px rgba(249,115,22,0.18)',
  },

  // Favoritos
  favBtn: {
    fontSize: '0.7rem',
    color: '#0369a1', background: '#f0f9ff',
    border: '1px solid #bae6fd', borderRadius: 6,
    textTransform: 'none', fontWeight: 600,
    padding: '4px 10px',
    '&:hover': { background: '#e0f2fe' },
  },
  favEmpty: {
    fontSize: '0.66rem', color: '#94a3b8',
    fontStyle: 'italic',
    padding: '4px 0',
  },

  // Pedido composto
  convoySplit: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 12,
  },
  convoyColumn: {
    background: '#fff',
    border: '2px solid #fdba74',
    borderRadius: 12,
    padding: '14px 14px',
    boxShadow: '0 2px 8px rgba(249,115,22,0.07)',
  },
  convoyBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: '#f97316', color: '#fff',
    padding: '4px 12px', borderRadius: 16,
    fontSize: '0.7rem', fontWeight: 800,
    letterSpacing: '0.02em',
    marginBottom: 12,
  },
  coordChip: {
    background: '#fff', border: '1px solid #e2e8f0',
    borderRadius: 6, padding: '6px 10px',
    fontSize: '0.7rem', color: '#475569',
    display: 'flex', alignItems: 'center', gap: 6,
    marginTop: 8,
  },

  // Avançado
  advancedToggle: {
    fontSize: '0.74rem', color: '#64748b',
    textTransform: 'none', fontWeight: 600,
    marginTop: 4,
    '&:hover': { background: 'transparent', color: '#0f172a' },
  },

  // Resumo
  summary: {
    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
    border: '1px solid #86efac',
    borderRadius: 10,
    padding: '12px 16px',
    display: 'flex', alignItems: 'center', gap: 14,
    marginTop: 8,
  },
  summaryIcon: {
    width: 36, height: 36, borderRadius: 18,
    background: '#16a34a', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  summaryText: {
    flex: 1, fontSize: '0.82rem', color: '#14532d',
    lineHeight: 1.4,
  },
  summaryStrong: { fontWeight: 800, color: '#166534' },

  error: {
    background: '#fee2e2', border: '1px solid #fca5a5',
    color: '#991b1b', padding: '10px 14px', borderRadius: 8,
    fontSize: '0.78rem', marginBottom: 12,
    display: 'flex', alignItems: 'center', gap: 8,
  },

  actions: {
    padding: '14px 24px',
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
    boxShadow: '0 -2px 8px rgba(15,23,42,0.04)',
  },
  primaryBtn: {
    background: 'linear-gradient(135deg, #fb923c, #f97316)',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    padding: '8px 22px',
    borderRadius: 8,
    boxShadow: '0 2px 6px rgba(249,115,22,0.25)',
    '&:hover': {
      background: 'linear-gradient(135deg, #f97316, #ea580c)',
    },
    '&:disabled': {
      background: '#cbd5e1', color: '#fff', boxShadow: 'none',
    },
  },
  cancelBtn: {
    textTransform: 'none', fontWeight: 600, color: '#64748b',
  },
}));

const FUEL_PRODUCTS = [
  { key: 'gasoleo_simples',   label: 'Gasóleo simples' },
  { key: 'gasoleo_aditivado', label: 'Gasóleo aditivado' },
  { key: 'gasolina_95',       label: 'Gasolina 95' },
  { key: 'gasolina_98',       label: 'Gasolina 98' },
  { key: 'jet_a1',            label: 'Jet A-1' },
  { key: 'outro',             label: 'Outro / múltiplo' },
];

// Capacidades reais usadas pela Move Express (confirmadas pelo documento de
// processo do cliente 2026-06-25 — tabela lista 20/24/25/35/36/60).
const CAPACITIES = [
  { value: 20, label: '20 m³', desc: 'rígido' },
  { value: 23, label: '23 m³', desc: '' },
  { value: 24, label: '24 m³', desc: '' },
  { value: 25, label: '25 m³', desc: '' },
  { value: 35, label: '35 m³', desc: '' },
  { value: 36, label: '36 m³', desc: '' },
  { value: 50, label: '50 m³', desc: '' },
  { value: 60, label: '60 m³', desc: '35+25 · 2 cisternas' },
];

// Clientes finais da Move Express (confirmados pelo operador 2026-06-25).
// Se aparecer outro, basta acrescentar aqui — sem mudanças noutros sítios.
const FINAL_CUSTOMERS = [
  { key: 'sonangol',  label: 'Sonangol',  shortRef: 'Son' },
  { key: 'total',     label: 'Total',     shortRef: 'Tot' },
  { key: 'pumangol',  label: 'Pumangol',  shortRef: 'Pum' },
  { key: 'sonangalp', label: 'Sonangalp', shortRef: 'Snp' },
];

const selectMenuProps = {
  disablePortal: true,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// 18 províncias de Angola. Usado quando o cliente final ainda não confirmou
// o PA exacto — o operador escolhe só a província e o PA fica para preencher
// no popup "Carregou" (motorista recebe a Ordem Sonangol com o local final).
// As coordenadas são da capital provincial — servem para que o planeamento
// de rota tenha um destino concreto (aproximado). Operador refina depois.
const ANGOLA_PROVINCES_COORDS = {
  'Bengo':          { lat: -8.4500,  lng: 13.6500 },
  'Benguela':       { lat: -12.5763, lng: 13.4055 },
  'Bié':            { lat: -12.3900, lng: 16.9300 },
  'Cabinda':        { lat: -5.5500,  lng: 12.2000 },
  'Cuando-Cubango': { lat: -14.6600, lng: 17.6900 },
  'Cuanza-Norte':   { lat: -9.3000,  lng: 14.9100 },
  'Cuanza-Sul':     { lat: -11.2000, lng: 14.2700 },
  'Cunene':         { lat: -16.9100, lng: 15.7400 },
  'Huambo':         { lat: -12.7747, lng: 15.7361 },
  'Huíla':          { lat: -14.9200, lng: 13.9200 },
  'Luanda':         { lat: -8.8390,  lng: 13.2894 },
  'Lunda-Norte':    { lat: -9.4100,  lng: 19.4100 },
  'Lunda-Sul':      { lat: -9.6600,  lng: 20.3900 },
  'Malanje':        { lat: -9.5400,  lng: 16.3400 },
  'Moxico':         { lat: -12.6000, lng: 20.3900 },
  'Namibe':         { lat: -15.1900, lng: 12.1500 },
  'Uíge':           { lat: -7.6100,  lng: 15.0600 },
  'Zaire':          { lat: -6.2700,  lng: 14.2400 },
};
const ANGOLA_PROVINCES = Object.keys(ANGOLA_PROVINCES_COORDS);

function suggestName(customerKey, dateStr, slotIdx) {
  const cust = FINAL_CUSTOMERS.find(c => c.key === customerKey) || FINAL_CUSTOMERS[0];
  const d = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
  const day = String(d.getDate()).padStart(2, '0');
  return `${cust.shortRef}-${day}${MONTHS_PT[d.getMonth()]}-S${slotIdx}`;
}

function productLabel(key) {
  return FUEL_PRODUCTS.find(p => p.key === key)?.label || '—';
}

function emptyOT() {
  return {
    name: '',
    fuelProduct: '',
    capacityM3: 35,
    expectedLitres: '',
    overrideLitres: false,
    pickup: { locationName: '', address: '', latitude: null, longitude: null },
    delivery: { locationName: '', address: '', latitude: null, longitude: null },
    // PA por confirmar (só relevante em modo edição): cliente final só
    // indica província, PA exacto é preenchido depois. Default false na
    // criação porque a criação nem mostra estes campos (caixa azul info
    // a explicar que o PA é revelado no carregamento).
    deliveryTbd: false,
    deliveryProvince: 'Luanda',
    unloadingTime: '',
    notes: '',
    instructions: '',
  };
}

function FuelOrderWizard({ open, onClose, editOrder = null, viewOnly = false, lang = 'pt' }) {
  // Helper de tradução curto para reduzir verbosidade nos JSX.
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  const country = clientSettings?.defaultCountry || 'AO';
  const isEdit = !!editOrder;

  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const [serviceDate, setServiceDate] = useState(todayStr);
  const [finalCustomer, setFinalCustomer] = useState('sonangol');
  const [isConvoy, setIsConvoy] = useState(false);
  const [coordinatedDelivery, setCoordinatedDelivery] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [otA, setOtA] = useState(() => emptyOT());
  const [otB, setOtB] = useState(() => emptyOT());
  const [favorites, setFavorites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Pedido de confirmação para sincronizar a rota quando o destino de uma OT
  // já despachada muda. Guarda { routeId, otId, lat, lng, name } ou null.
  const [syncPrompt, setSyncPrompt] = useState(null);

  // Operador confirmou: recalcula a geometria da rota para o novo destino.
  async function runRouteSync() {
    if (!syncPrompt) return;
    setSaving(true);
    try {
      const sync = await recomputeRouteAfterOTChange({ ...syncPrompt, token });
      if (!sync?.ok) {
        setSaving(false);
        setSyncPrompt(null);
        setError(T(
          `✅ OT guardada. ⚠️ Não foi possível atualizar a rota${sync?.error ? ` (${sync.error})` : ''}. Pode recalcular no Planeamento.`,
          `✅ Order saved. ⚠️ Could not update the route${sync?.error ? ` (${sync.error})` : ''}. You can recompute it in Planning.`
        ));
        return; // mantém o wizard aberto a mostrar o aviso
      }
    } catch (e) {
      setSaving(false);
      setSyncPrompt(null);
      setError(T(
        '✅ OT guardada. ⚠️ Falha ao atualizar a rota. Pode recalcular no Planeamento.',
        '✅ Order saved. ⚠️ Failed to update the route. You can recompute it in Planning.'
      ));
      return;
    }
    setSyncPrompt(null);
    onClose(true); // tudo OK — fecha e refresca a listagem/mapa
  }

  // Operador recusou: a OT fica guardada, a rota mantém o destino anterior.
  function skipRouteSync() {
    setSyncPrompt(null);
    onClose(true);
  }

  // Helper: a partir do `customer.shortRef` no início do nome inferimos qual
  // o cliente final escolhido na criação. Necessário para pré-popular o
  // dropdown na edição (não temos campo dedicado para finalCustomer).
  function detectCustomerFromName(name) {
    if (!name) return 'sonangol';
    const match = FINAL_CUSTOMERS.find(c => name.startsWith(c.shortRef + '-'));
    return match?.key || 'sonangol';
  }

  // Reset ao abrir.
  useEffect(() => {
    if (!open) return;
    if (isEdit && editOrder) {
      // ── Pré-popular a partir da OT existente ───────────────────
      const ot = editOrder;
      const cap = Number(ot.cisternCapacityM3) || 35;
      // Em edição lê expectedLitres (esperados); se vazio (OTs antigas que
      // usavam loadingOrderQty na criação), faz fallback para loadingOrderQty.
      const litres = ot.expectedLitres != null
        ? Number(ot.expectedLitres)
        : (ot.loadingOrderQty != null ? Number(ot.loadingOrderQty) : null);
      const nominalLitres = cap * 1000;
      // Se a OT tem litros guardados E são diferentes do nominal, fica em
      // modo "ajustar". Senão volta ao automático.
      const overrideLitres = litres != null && Math.abs(litres - nominalLitres) > 0.5;
      // Detecta PA por confirmar: tem deliveryProvince guardada E o nome
      // tem prefixo "PA por confirmar".
      const tbd = !!ot.deliveryProvince
        && (ot.deliveryName || '').startsWith('PA por confirmar');
      // Remove sufixo "Cliente: X" das notas (que tinha sido adicionado
      // automaticamente na criação).
      const cleanNotes = (ot.notes || '').replace(/\s*·?\s*Cliente:.*$/i, '').trim();
      setOtA({
        ...emptyOT(),
        name: ot.customRef || '',
        fuelProduct: ot.fuelProduct || '',
        capacityM3: cap,
        expectedLitres: overrideLitres ? String(litres) : '',
        overrideLitres,
        pickup: {
          locationName: ot.pickupName || '',
          address: ot.pickupAddress || '',
          latitude: ot.pickupLat != null ? Number(ot.pickupLat) : null,
          longitude: ot.pickupLng != null ? Number(ot.pickupLng) : null,
        },
        delivery: tbd ? { locationName: '', address: '', latitude: null, longitude: null } : {
          locationName: ot.deliveryName || '',
          address: ot.deliveryAddress || '',
          latitude: ot.deliveryLat != null ? Number(ot.deliveryLat) : null,
          longitude: ot.deliveryLng != null ? Number(ot.deliveryLng) : null,
        },
        deliveryTbd: tbd,
        deliveryProvince: ot.deliveryProvince || 'Luanda',
        unloadingTime: ot.unloadingTime != null ? String(ot.unloadingTime) : '',
        notes: cleanNotes,
        instructions: ot.deliveryInstructions || '',
      });
      // Em edição prefere o campo dedicado fuelCustomer (fonte de verdade);
      // se ausente (OTs antigas pré-feature) cai no parse do nome.
      setFinalCustomer(ot.fuelCustomer || detectCustomerFromName(ot.customRef));
      // Comboio: edita-se apenas a OT actual; o pedido composto fica info
      // (mostramos a flag mas sem partição em 2 cards).
      setIsConvoy(Array.isArray(ot.linkedToOtIds) && ot.linkedToOtIds.length > 0);
      setCoordinatedDelivery(!!ot.coordinatedDelivery);
      setServiceDate(ot.serviceDate
        ? new Date(ot.serviceDate).toISOString().slice(0, 10)
        : todayStr);
    } else {
      // ── Modo criação ───────────────────────────────────────────
      setOtA({ ...emptyOT(), name: suggestName('sonangol', todayStr, 1) });
      setOtB({ ...emptyOT(), name: suggestName('sonangol', todayStr, 2) });
      setFinalCustomer('sonangol');
      setIsConvoy(false);
      setCoordinatedDelivery(false);
      setServiceDate(todayStr);
    }
    setShowAdvanced(false);
    setError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, editOrder?.id]);

  // Re-sugerir nome quando cliente/data muda (só se o operador não tiver editado).
  // Em modo edição NÃO faz auto-rename — o nome veio da OT existente e o
  // operador edita à mão.
  useEffect(() => {
    if (isEdit) return;
    setOtA(prev => {
      const oldSuggestPrefixes = FINAL_CUSTOMERS.map(c => c.shortRef);
      const editedManually = prev.name && !oldSuggestPrefixes.some(p => prev.name.startsWith(p));
      if (editedManually) return prev;
      return { ...prev, name: suggestName(finalCustomer, serviceDate, 1) };
    });
    setOtB(prev => {
      const oldSuggestPrefixes = FINAL_CUSTOMERS.map(c => c.shortRef);
      const editedManually = prev.name && !oldSuggestPrefixes.some(p => prev.name.startsWith(p));
      if (editedManually) return prev;
      return { ...prev, name: suggestName(finalCustomer, serviceDate, 2) };
    });
  }, [finalCustomer, serviceDate, isEdit]);

  // Carrega favoritos do tenant.
  useEffect(() => {
    if (!open || !token) return;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch('/planner-api/locations', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        // Ordenar por ordem alfabética (nome, senão morada) — facilita a pesquisa.
        const arr = Array.isArray(json) ? json : (json.locations || []);
        const sorted = [...arr].sort((a, b) =>
          String(a.name || a.address || '').localeCompare(String(b.name || b.address || ''), 'pt', { sensitivity: 'base' }));
        if (!cancel) setFavorites(sorted);
      } catch (_) { /* silencioso */ }
    })();
    return () => { cancel = true; };
  }, [open, token]);

  function patchOT(target, patch) {
    if (target === 'A') setOtA(prev => ({ ...prev, ...patch }));
    else setOtB(prev => ({ ...prev, ...patch }));
  }

  function patchLocation(target, side, patch) {
    const setter = target === 'A' ? setOtA : setOtB;
    setter(prev => ({ ...prev, [side]: { ...prev[side], ...patch } }));
  }

  function applyFavorite(target, side, fav) {
    patchLocation(target, side, {
      locationName: fav.name || '',
      address: fav.address || '',
      latitude: fav.latitude != null ? Number(fav.latitude) : null,
      longitude: fav.longitude != null ? Number(fav.longitude) : null,
    });
  }

  function applyAddressGeocode(target, side, geo) {
    patchLocation(target, side, {
      address: geo.name,
      latitude: geo.coordinates?.[1] ?? null,
      longitude: geo.coordinates?.[0] ?? null,
    });
  }

  function computeLitres(ot) {
    if (ot.overrideLitres && ot.expectedLitres !== '') return Number(ot.expectedLitres);
    return Number(ot.capacityM3 || 0) * 1000;
  }

  function buildPayload(ot, linkedIds) {
    const litres = computeLitres(ot);
    return {
      customRef: ot.name || undefined,
      serviceType: 'fuel',
      serviceDate: serviceDate || null,
      pickupName: ot.pickup.locationName || undefined,
      pickupAddress: ot.pickup.address || undefined,
      pickupLat: ot.pickup.latitude || undefined,
      pickupLng: ot.pickup.longitude || undefined,
      // Delivery: CRIAÇÃO → undefined (PA revelado depois no "Carregou").
      // EDIÇÃO → respeitar modo TBD/normal.
      deliveryName: !isEdit
        ? undefined
        : (ot.deliveryTbd
            ? `PA por confirmar — ${ot.deliveryProvince}`
            : (ot.delivery.locationName || undefined)),
      deliveryAddress: !isEdit
        ? undefined
        : (ot.deliveryTbd
            ? `Província: ${ot.deliveryProvince}, Angola`
            : (ot.delivery.address || undefined)),
      deliveryLat: !isEdit
        ? undefined
        : (ot.deliveryTbd
            ? (ANGOLA_PROVINCES_COORDS[ot.deliveryProvince]?.lat || undefined)
            : (ot.delivery.latitude || undefined)),
      deliveryLng: !isEdit
        ? undefined
        : (ot.deliveryTbd
            ? (ANGOLA_PROVINCES_COORDS[ot.deliveryProvince]?.lng || undefined)
            : (ot.delivery.longitude || undefined)),
      deliveryProvince: !isEdit
        ? undefined
        : (ot.deliveryTbd ? ot.deliveryProvince : (ot.delivery.address ? null : ot.deliveryProvince)),
      deliveryInstructions: ot.instructions || undefined,
      unloadingTime: ot.unloadingTime !== '' && ot.unloadingTime != null ? Number(ot.unloadingTime) : null,
      notes: ot.notes
        ? `${ot.notes}${finalCustomer ? ` · Cliente: ${FINAL_CUSTOMERS.find(c => c.key === finalCustomer)?.label}` : ''}`
        : (finalCustomer ? `Cliente: ${FINAL_CUSTOMERS.find(c => c.key === finalCustomer)?.label}` : undefined),
      returnHome: false,
      priority: 0,
      fuelProduct: ot.fuelProduct || undefined,
      cisternCapacityM3: ot.capacityM3 ? Number(ot.capacityM3) : undefined,
      // Litros esperados — campo separado para não disparar "hasLoaded"
      // antes do operador clicar "Carregou" no FuelOTPanel. O loadingOrderQty
      // continua reservado para a quantidade REAL carregada.
      expectedLitres: litres || undefined,
      // Cliente final (sonangol/total/pumangol/sonangalp) — campo dedicado
      // em vez de parse das notes (fragil). Usado pelo popup "Carregou"
      // para mostrar "Nº do selo" só quando fuelCustomer==='total'.
      fuelCustomer: finalCustomer || undefined,
      linkedToOtIds: linkedIds && linkedIds.length > 0 ? linkedIds : undefined,
      coordinatedDelivery: isConvoy ? !!coordinatedDelivery : undefined,
    };
  }

  function validate() {
    const errs = [];
    function checkOT(ot, label) {
      if (!ot.fuelProduct) errs.push(`${label}: produto em falta`);
      if (!ot.capacityM3) errs.push(`${label}: capacidade em falta`);
      if (!ot.pickup.locationName && !ot.pickup.address) errs.push(`${label}: onde carrega em falta`);
      // Delivery (PA): NUNCA é exigido — nas OT de combustível o ponto de
      // descarga real só é revelado no carregamento ("Carregou", via Ordem do
      // cliente). Por isso editar uma OT ainda sem PA não deve ser bloqueado.
      // Só validamos a província quando o modo "PA por confirmar" está activo.
      if (isEdit && ot.deliveryTbd && !ot.deliveryProvince) {
        errs.push(`${label}: província em falta`);
      }
    }
    checkOT(otA, isConvoy ? 'OT A' : 'OT');
    if (isConvoy) checkOT(otB, 'OT B');
    return errs;
  }

  async function handleSubmit() {
    setError(null);
    const errs = validate();
    if (errs.length) { setError(errs.join(' · ')); return; }
    setSaving(true);
    try {
      if (isEdit) {
        // Edição: PUT à OT actual. Preserva linkedToOtIds existentes (não
        // mexemos no comboio em modo edit). coordinatedDelivery vai como
        // estado actual.
        const data = buildPayload(otA, editOrder.linkedToOtIds || null);
        await dispatch(updateOrder({
          token,
          id: editOrder._id || editOrder.id,
          data,
        })).unwrap();

        // ── Sincronização morada↔rota (destino) ──────────────────────
        // O wizard fuel não passava pela guarda do OrderForm, por isso ao
        // mudar o DESTINO de uma OT já com rota, a OT atualizava mas a rota
        // no mapa continuava a apontar ao destino antigo. Aqui, se o destino
        // mudou E a OT tem rota recalculável (draft/planned/in_progress),
        // recalculamos a geometria (HERE) e persistimos — a app do motorista
        // recebe `route:updated` e passa a navegar para a nova morada.
        const newLat = otA.delivery?.latitude;
        const newLng = otA.delivery?.longitude;
        const newName = otA.delivery?.locationName || '';
        const hasNewCoords = Number.isFinite(Number(newLat)) && Number.isFinite(Number(newLng));
        const destChanged = hasNewCoords && (
          Math.abs(Number(newLat) - Number(editOrder.deliveryLat)) > 1e-6 ||
          Math.abs(Number(newLng) - Number(editOrder.deliveryLng)) > 1e-6 ||
          newName !== (editOrder.deliveryName || '')
        );
        if (editOrder.assignedRouteId && !otA.deliveryTbd && destChanged) {
          // NÃO recalcula silenciosamente (está em produção). A OT já foi
          // guardada; perguntamos ao operador se quer atualizar também a
          // rota no mapa. A decisão é tratada em runRouteSync/skipRouteSync.
          setSaving(false);
          setSyncPrompt({
            routeId: editOrder.assignedRouteId,
            otId: editOrder._id || editOrder.id,
            lat: Number(newLat),
            lng: Number(newLng),
            name: newName,
          });
          return; // aguarda a decisão; o wizard mantém-se aberto com o prompt
        }
      } else if (!isConvoy) {
        await dispatch(createOrder({ token, data: buildPayload(otA, null) })).unwrap();
      } else {
        const createdA = await dispatch(createOrder({ token, data: buildPayload(otA, null) })).unwrap();
        const idA = createdA?.id || createdA?._id;
        const createdB = await dispatch(createOrder({ token, data: buildPayload(otB, idA ? [idA] : null) })).unwrap();
        const idB = createdB?.id || createdB?._id;
        if (idA && idB) {
          await dispatch(updateOrder({ token, id: idA, data: { linkedToOtIds: [idB] } })).unwrap();
        }
      }
      onClose(true);
    } catch (err) {
      console.error('FuelOrderWizard submit error:', err);
      setError((err && (err.error || err.message)) || 'Erro ao gravar a OT.');
    } finally {
      setSaving(false);
    }
  }

  const litresA = computeLitres(otA);
  const litresB = computeLitres(otB);
  const totalLitres = isConvoy ? litresA + litresB : litresA;
  const customerLabel = FINAL_CUSTOMERS.find(c => c.key === finalCustomer)?.label || '—';

  return (
    <>
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        // Não fechar por clique no backdrop nem ESC — evita perder o que já
        // foi preenchido no wizard. Só fecha via botões (Cancelar / X / criar).
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose(false);
      }}
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth={isConvoy ? 'lg' : 'md'}
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <Box className={classes.titleLeft}>
          <Box className={classes.titleIconBox}>
            <LocalGasStationIcon style={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography className={classes.titleText}>
              {viewOnly
                ? T('Consultar Ordem de Combustível', 'View Fuel Order')
                : (isEdit ? T('Editar Ordem de Combustível', 'Edit Fuel Order') : T('Nova Ordem de Combustível', 'New Fuel Order'))}
            </Typography>
            <Typography className={classes.titleSub}>
              {isEdit
                ? `${T('Distribuição', 'Distribution')} · #${editOrder.id || editOrder._id}${isConvoy ? ' · LINK' : ''}`
                : `${T('Distribuição', 'Distribution')} · ${isConvoy ? T('LINK · pedido composto (2 cisternas)', 'LINK · composite request (2 cisterns)') : T('1 cisterna', '1 cistern')}`}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={() => onClose(false)} style={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content} dividers>
        {/* Modo consulta: banner + fieldset disabled (bloqueia teclado em todos
            os campos nativos) + pointer-events:none (bloqueia rato nos Select/
            Switch/pickers à base de div). OT entregue não pode ser editada. */}
        {viewOnly && (
          <Box style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', marginBottom: 12, borderRadius: 8,
            background: '#eff6ff', border: '1px solid #bfdbfe',
            fontSize: '0.78rem', fontWeight: 700, color: '#1e40af',
          }}>
            <span style={{ fontSize: 15 }}>🔒</span>
            {T('Modo consulta — esta OT já foi entregue e não pode ser editada.',
               'View mode — this order is already delivered and cannot be edited.')}
          </Box>
        )}
        <fieldset disabled={viewOnly} style={{
          border: 'none', margin: 0, padding: 0, minInlineSize: 'auto',
          pointerEvents: viewOnly ? 'none' : undefined,
        }}>
        {error && (
          <Box className={classes.error}>
            <span style={{ fontSize: 16 }}>⚠</span>{error}
          </Box>
        )}

        {/* Painel KPI do topo (estilo cisternas no FleetView): wrap único com
            gradient suave + cards internos com label uppercase + valor monospace. */}
        <Box className={classes.topRowWrap}>
          <Box className={classes.topCard}>
            <Typography className={classes.topLabel}>
              <EventIcon style={{ fontSize: 11 }} /> {T('Data do serviço', 'Service date')}
            </Typography>
            <TextField
              size="small" variant="standard" type="date"
              value={serviceDate}
              onChange={e => setServiceDate(e.target.value)}
              InputProps={{
                disableUnderline: true,
                style: {
                  fontSize: '0.92rem', fontWeight: 800, color: '#0f172a',
                  fontFamily: 'ui-monospace, Menlo, monospace',
                },
              }}
              fullWidth
            />
          </Box>

          <Box className={classes.topCard}>
            <Typography className={classes.topLabel}>
              <BusinessIcon style={{ fontSize: 11 }} /> {T('Cliente final', 'Final customer')}
            </Typography>
            <FormControl size="small" variant="standard" fullWidth>
              <Select
                value={finalCustomer}
                onChange={e => setFinalCustomer(e.target.value)}
                MenuProps={selectMenuProps}
                disableUnderline
                style={{
                  fontSize: '0.95rem', fontWeight: 800, color: '#0f172a',
                }}
              >
                {FINAL_CUSTOMERS.map(c => (
                  <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Pedido composto REMOVIDO: um camião pode rebocar mais do que uma
              cisterna, por isso já não se criam 2 OTs ligadas. É sempre 1 OT;
              as cisternas (1 ou 2) escolhem-se na ATRIBUIÇÃO do veículo. */}
        </Box>

        {/* Blocos da(s) OT(s) */}
        {/* Em edição: nunca mostra a split de 2 cards. Edita-se SÓ esta OT. */}
        {(!isConvoy || isEdit) ? (
          <OTBlock
            ot={otA}
            favorites={favorites}
            country={country}
            patchOT={p => patchOT('A', p)}
            patchLocation={(side, p) => patchLocation('A', side, p)}
            applyFavorite={(side, f) => applyFavorite('A', side, f)}
            applyAddressGeocode={(side, g) => applyAddressGeocode('A', side, g)}
            classes={classes}
            isEdit={isEdit}
            lang={lang}
          />
        ) : (
          <>
            <Box className={classes.convoySplit}>
              <Box className={classes.convoyColumn}>
                <span className={classes.convoyBadge}>
                  <LocalShippingIcon style={{ fontSize: 13 }} /> OT A · 1.ª cisterna
                </span>
                <OTBlock
                  ot={otA}
                  favorites={favorites}
                  country={country}
                  patchOT={p => patchOT('A', p)}
                  patchLocation={(side, p) => patchLocation('A', side, p)}
                  applyFavorite={(side, f) => applyFavorite('A', side, f)}
                  applyAddressGeocode={(side, g) => applyAddressGeocode('A', side, g)}
                  classes={classes}
                  compact
                  lang={lang}
                />
              </Box>
              <Box className={classes.convoyColumn}>
                <span className={classes.convoyBadge}>
                  <LocalShippingIcon style={{ fontSize: 13 }} /> OT B · 2.ª cisterna
                </span>
                <OTBlock
                  ot={otB}
                  favorites={favorites}
                  country={country}
                  patchOT={p => patchOT('B', p)}
                  patchLocation={(side, p) => patchLocation('B', side, p)}
                  applyFavorite={(side, f) => applyFavorite('B', side, f)}
                  applyAddressGeocode={(side, g) => applyAddressGeocode('B', side, g)}
                  classes={classes}
                  compact
                  isEdit={isEdit}
                  lang={lang}
                />
              </Box>
            </Box>
            <Box style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={coordinatedDelivery}
                    onChange={e => setCoordinatedDelivery(e.target.checked)}
                  />
                }
                label={
                  <Typography style={{ fontSize: '0.78rem' }}>
                    <strong>Entrega coordenada</strong> &nbsp;
                    <span style={{ color: '#64748b' }}>
                      — os 2 motoristas vêem a entrega como única na app
                    </span>
                  </Typography>
                }
              />
            </Box>
          </>
        )}

        {/* Observações — campo visível e dedicado. Reaproveita o `notes` da OT
            (aparece na listagem e no PDF do motorista). Situações específicas:
            ex.: serviço em modo de teste. Num composto, escreve nas 2 OTs. */}
        <Box className={classes.card} style={{ marginTop: 8 }}>
          <Box className={classes.cardHeader}>
            <Box className={classes.iconCircle}>
              <NotesIcon style={{ fontSize: 14 }} />
            </Box>
            <Typography className={classes.cardTitle}>
              {T('OBSERVAÇÕES', 'NOTES')}
            </Typography>
            <Typography className={classes.cardTitleHint}>{T('situações específicas', 'specific situations')}</Typography>
          </Box>
          <TextField
            size="small" variant="outlined" fullWidth
            placeholder={T('Ex.: serviço em modo de teste, situação específica…', 'E.g. test-mode service, specific situation…')}
            value={otA.notes}
            onChange={e => { patchOT('A', { notes: e.target.value }); if (isConvoy) patchOT('B', { notes: e.target.value }); }}
            multiline rowsMax={3}
            helperText={T('Fica visível na listagem de OT e no PDF do motorista.', 'Shown in the order list and the driver PDF.')}
          />
        </Box>

        {/* Opções avançadas */}
        <Button
          className={classes.advancedToggle}
          size="small"
          startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setShowAdvanced(v => !v)}
        >
          {T('Opções avançadas (tempo de descarga)', 'Advanced options (unloading time)')}
        </Button>
        <Collapse in={showAdvanced}>
          <Box className={classes.card} style={{ marginTop: 8 }}>
            <Box className={classes.row2}>
              <TextField
                size="small" variant="outlined" type="number"
                label={T('Tempo de descarga (min)', 'Unloading time (min)')}
                value={otA.unloadingTime}
                onChange={e => patchOT('A', { unloadingTime: e.target.value })}
                helperText={T('Tempo médio na PA (opcional)', 'Average time at delivery point (optional)')}
              />
            </Box>
          </Box>
        </Collapse>

        {/* Resumo (só na criação/edição — não em consulta) */}
        {!viewOnly && ((otA.fuelProduct && otA.capacityM3) || (isConvoy && otB.fuelProduct && otB.capacityM3)) && (
          <Box className={classes.summary}>
            <Box className={classes.summaryIcon}>
              <CheckIcon style={{ fontSize: 20 }} />
            </Box>
            <Typography className={classes.summaryText}>
              Vai criar <span className={classes.summaryStrong}>{isConvoy ? '2 OTs (composto)' : '1 OT'}</span> de{' '}
              <span className={classes.summaryStrong}>{totalLitres.toLocaleString('pt-PT')} L</span>
              {' '}({isConvoy ? `${productLabel(otA.fuelProduct)} + ${productLabel(otB.fuelProduct)}` : productLabel(otA.fuelProduct)})
              {' '}para <span className={classes.summaryStrong}>{customerLabel}</span>
              {serviceDate && <> · entrega <span className={classes.summaryStrong}>{new Date(serviceDate + 'T00:00:00').toLocaleDateString('pt-PT')}</span></>}.
            </Typography>
          </Box>
        )}
        </fieldset>
      </DialogContent>

      <DialogActions className={classes.actions}>
        {viewOnly ? (
          <Button onClick={() => onClose(false)} className={classes.primaryBtn} startIcon={<CloseIcon />}>
            {T('Fechar', 'Close')}
          </Button>
        ) : (
          <>
            <Button onClick={() => onClose(false)} disabled={saving} className={classes.cancelBtn}>
              {T('Cancelar', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              className={classes.primaryBtn}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <LocalGasStationIcon />}
            >
              {saving
                ? T('A gravar...', 'Saving...')
                : (isEdit
                    ? T('Guardar alterações', 'Save changes')
                    : (isConvoy ? T('Criar composto (2 OTs)', 'Create composite (2 OTs)') : T('Criar Ordem de Combustível', 'Create Fuel Order')))}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

    {/* Confirmação: destino alterado numa OT já despachada → atualizar a rota?
        Não mexe na rota sem o operador confirmar (segurança em produção). */}
    <Dialog open={!!syncPrompt} maxWidth="xs" fullWidth
      onClose={(_e, reason) => { if (reason === 'backdropClick') return; }}>
      <DialogTitle disableTypography className={classes.title}>
        <Box className={classes.titleLeft}>
          <Box className={classes.titleIconBox}><RoomIcon style={{ fontSize: 22 }} /></Box>
          <Typography className={classes.titleText}>
            {T('Atualizar a rota no mapa?', 'Update the map route?')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: '18px 22px' }}>
        <Typography style={{ fontSize: '0.9rem', color: '#334155', lineHeight: 1.5 }}>
          {T(
            'O destino desta OT foi alterado e a OT já ficou guardada. Queres atualizar também a rota apresentada no mapa para o novo destino?',
            'This order’s destination changed and the order was saved. Do you also want to update the route shown on the map to the new destination?'
          )}
        </Typography>
      </DialogContent>
      <DialogActions style={{ padding: '10px 18px 16px' }}>
        <Button onClick={skipRouteSync} disabled={saving} style={{ textTransform: 'none' }}>
          {T('Só guardar a OT', 'Keep order only')}
        </Button>
        <Button onClick={runRouteSync} disabled={saving} variant="contained"
          style={{ textTransform: 'none', background: '#ea580c', color: '#fff' }}>
          {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : T('Sim, atualizar rota', 'Yes, update route')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

// ─── Bloco de 1 OT ──────────────────────────────────────────────────
function OTBlock({ ot, favorites, country, patchOT, patchLocation, applyFavorite, applyAddressGeocode, classes, compact, isEdit, lang = 'pt' }) {
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const litres = ot.overrideLitres && ot.expectedLitres !== ''
    ? Number(ot.expectedLitres)
    : Number(ot.capacityM3 || 0) * 1000;

  return (
    <>
      <Box className={`${classes.card} ${classes.cardFuel}`}>
        <Box className={classes.cardHeader}>
          <Box className={classes.iconCircle}>
            <LocalGasStationIcon style={{ fontSize: 14 }} />
          </Box>
          <Typography className={`${classes.cardTitle} ${classes.cardTitleFuel}`}>
            O QUE ENTREGAR
          </Typography>
          <Typography className={classes.cardTitleHint}>produto · capacidade · litros</Typography>
        </Box>

        {!compact && (
          <Box style={{ marginBottom: 12 }}>
            <TextField
              size="small" variant="outlined" fullWidth
              label="Nome / Referência"
              value={ot.name}
              onChange={e => patchOT({ name: e.target.value })}
              helperText="Auto-sugerido a partir do cliente e data. Editável."
            />
          </Box>
        )}

        <Box className={classes.row3}>
          <FormControl size="small" variant="outlined">
            <InputLabel>Produto *</InputLabel>
            <Select
              label="Produto *"
              value={ot.fuelProduct}
              onChange={e => patchOT({ fuelProduct: e.target.value })}
              MenuProps={selectMenuProps}
            >
              <MenuItem value=""><em>— escolher —</em></MenuItem>
              {FUEL_PRODUCTS.map(p => (
                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" variant="outlined">
            <InputLabel>Capacidade *</InputLabel>
            <Select
              label="Capacidade *"
              value={ot.capacityM3}
              onChange={e => patchOT({ capacityM3: Number(e.target.value) })}
              MenuProps={selectMenuProps}
            >
              {CAPACITIES.map(c => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label} {c.desc && <span style={{ color: '#94a3b8', marginLeft: 4 }}>· {c.desc}</span>}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Volume — mini-card KPI (estilo painel das cisternas na Frota) */}
          {!ot.overrideLitres ? (
            <Box className={classes.litresBox}>
              <Box style={{ display: 'flex', flexDirection: 'column' }}>
                <span className={classes.litresLabel}>Volume</span>
                <span className={classes.litresValue}>
                  ≈ {litres.toLocaleString('pt-PT')} <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>L</span>
                </span>
              </Box>
              <Tooltip title="Ajustar manualmente">
                <Button
                  size="small"
                  className={classes.litresEditBtn}
                  onClick={() => patchOT({ overrideLitres: true, expectedLitres: String(litres) })}
                >
                  Ajustar
                </Button>
              </Tooltip>
            </Box>
          ) : (
            <Box className={classes.litresInputWrap}>
              <Box style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <span className={classes.litresLabel}>Volume</span>
                <Box style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <TextField
                    size="small" type="number"
                    value={ot.expectedLitres}
                    onChange={e => patchOT({ expectedLitres: e.target.value })}
                    autoFocus
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: '1.0rem', fontWeight: 800, color: '#0f172a',
                        fontFamily: 'ui-monospace, Menlo, monospace',
                        lineHeight: 1.15,
                      },
                    }}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 700 }}>L</span>
                </Box>
              </Box>
              <Tooltip title="Voltar ao automático">
                <IconButton size="small" onClick={() => patchOT({ overrideLitres: false, expectedLitres: '' })}>
                  <CloseIcon style={{ fontSize: 14, color: '#9a3412' }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      {/* CRIAÇÃO: 1 só card (Onde vai buscar) + caixa info azul a explicar
          que o PA é revelado no carregamento.
          EDIÇÃO: 2 cards (Onde vai buscar + Onde descarrega), porque o
          operador pode agora preencher o PA quando soube dele. */}
      <Box className={isEdit ? classes.row2 : undefined}>
        <LocationCard
          title={T('Onde vai buscar combustível', 'Fuel pickup location')}
          subtitle={T('Terminal Sonangol — TCPL, IBV1, IBV2…', 'Sonangol terminal — TCPL, IBV1, IBV2…')}
          iconClass={classes.iconCircle}
          icon={<LocalGasStationIcon style={{ fontSize: 16 }} />}
          loc={ot.pickup}
          favorites={favorites}
          country={country}
          onChangeName={v => patchLocation('pickup', { locationName: v })}
          onChangeAddress={v => patchLocation('pickup', { address: v })}
          onAddressSelect={geo => applyAddressGeocode('pickup', geo)}
          onPickFavorite={f => applyFavorite('pickup', f)}
          classes={classes}
        />
        {isEdit ? (
          <LocationCard
            title={T('Onde descarrega', 'Delivery location')}
            subtitle={T('PA do cliente final — preenche quando souberes', 'Customer delivery point — fill in when known')}
            iconClass={classes.iconCircleNeutral}
            icon={<RoomIcon style={{ fontSize: 16 }} />}
            loc={ot.delivery}
            favorites={favorites}
            country={country}
            onChangeName={v => patchLocation('delivery', { locationName: v })}
            onChangeAddress={v => patchLocation('delivery', { address: v })}
            onAddressSelect={geo => applyAddressGeocode('delivery', geo)}
            onPickFavorite={f => applyFavorite('delivery', f)}
            classes={classes}
            // "PA por confirmar" — só província. Só faz sentido para descarga.
            tbd={ot.deliveryTbd}
            onToggleTbd={v => patchOT({ deliveryTbd: v })}
            province={ot.deliveryProvince}
            onChangeProvince={v => patchOT({ deliveryProvince: v })}
          />
        ) : (
          // Caixa informativa azul — substituí o card "Onde descarrega".
          // O PA é revelado no terminal de carga e preenchido no "Carregou".
          <Box style={{
            marginTop: 12, padding: '14px 16px', borderRadius: 12,
            background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 80%)',
            border: '1px dashed #93c5fd',
            display: 'flex', alignItems: 'flex-start', gap: 12,
          }}>
            <Box style={{
              width: 32, height: 32, borderRadius: 16,
              background: '#dbeafe', color: '#1e40af',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <RoomIcon style={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography style={{
                fontSize: '0.74rem', fontWeight: 800, color: '#1e40af',
                textTransform: 'uppercase', letterSpacing: 0.5,
                marginBottom: 4,
              }}>
                {T('PA de descarga — preenchido no carregamento', 'Delivery point — filled at loading')}
              </Typography>
              <Typography style={{
                fontSize: '0.78rem', color: '#1e3a8a', lineHeight: 1.45,
              }}>
                {lang === 'pt' ? (
                  <>
                    Na criação da OT só precisas do <strong>local de carga</strong>.
                    O PA exacto é revelado pela Ordem Sonangol quando o motorista
                    chega ao terminal — preenche-o no popup <strong>"Carregou"</strong>
                    e o sistema actualiza a rota.
                  </>
                ) : (
                  <>
                    When creating the OT you only need the <strong>pickup location</strong>.
                    The exact delivery point is revealed by the Sonangol Order when the driver
                    arrives at the terminal — fill it in the <strong>"Loaded"</strong> popup
                    and the system updates the route.
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

function LocationCard({
  title, subtitle, icon, iconClass,
  loc, favorites, country,
  onChangeName, onChangeAddress, onAddressSelect, onPickFavorite,
  classes,
  // Props opcionais (só usados no card de descarga):
  tbd, onToggleTbd, province, onChangeProvince,
}) {
  const [anchor, setAnchor] = useState(null);
  const [favQuery, setFavQuery] = useState('');
  const [favDir, setFavDir] = useState('asc'); // ordenação A→Z / Z→A
  const supportsTbd = typeof onToggleTbd === 'function';

  // Pesquisa de favoritos — filtra por nome + morada, sem acentos nem
  // maiúsculas/minúsculas. A caixa de pesquisa só aparece quando há favoritos
  // suficientes para justificar (com poucos, a lista simples chega).
  const norm = (s) => (s || '').toString().normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '').toLowerCase();
  const showFavSearch = favorites.length > 1;
  const filteredFavorites = useMemo(() => {
    const q = norm(favQuery).trim();
    const base = !q ? favorites : favorites.filter(f => norm(f.name).includes(q) || norm(f.address).includes(q));
    // A lista já vem ASC; para DESC basta inverter.
    return favDir === 'desc' ? [...base].reverse() : base;
  }, [favorites, favQuery, favDir]);
  const closeFav = () => { setAnchor(null); setFavQuery(''); };
  return (
    <Box className={classes.card}>
      <Box className={classes.cardHeader}>
        <Box className={iconClass}>{icon}</Box>
        <Box style={{ flex: 1 }}>
          <Typography className={classes.cardTitle}>{title.toUpperCase()}</Typography>
          <Typography className={classes.cardTitleHint}>{subtitle}</Typography>
        </Box>
      </Box>

      {/* Toggle "PA por confirmar — só província". Aparece só no card de
          descarga (quando recebe os props tbd/onToggleTbd). Reflecte o flow
          real: o cliente final muitas vezes só indica a província; o PA
          exacto vem na Ordem Sonangol depois do carregamento. */}
      {supportsTbd && (
        <Box style={{
          marginBottom: 10, padding: '6px 10px',
          background: tbd ? '#fef3c7' : '#f8fafc',
          border: `1px solid ${tbd ? '#fbbf24' : '#e2e8f0'}`,
          borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box>
            <Typography style={{
              fontSize: '0.66rem', fontWeight: 800,
              color: tbd ? '#92400e' : '#475569',
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              PA por confirmar
            </Typography>
            <Typography style={{ fontSize: '0.62rem', color: tbd ? '#92400e' : '#94a3b8', fontStyle: 'italic' }}>
              só sei a província — preencho o PA quando o motorista carregar
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={!!tbd}
            onChange={e => onToggleTbd(e.target.checked)}
          />
        </Box>
      )}

      {/* Modo "PA por confirmar" — só dropdown de província */}
      {supportsTbd && tbd ? (
        <FormControl size="small" variant="outlined" fullWidth>
          <InputLabel>Província *</InputLabel>
          <Select
            label="Província *"
            value={province || ''}
            onChange={e => onChangeProvince(e.target.value)}
            MenuProps={selectMenuProps}
          >
            {ANGOLA_PROVINCES.map(p => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <>
          <Box style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            {favorites.length > 0 ? (
              <Button
                size="small"
                className={classes.favBtn}
                startIcon={<StarIcon style={{ fontSize: 14 }} />}
                onClick={e => setAnchor(e.currentTarget)}
              >
                Favoritos ({favorites.length})
              </Button>
            ) : (
              <Typography className={classes.favEmpty}>Ainda sem favoritos guardados</Typography>
            )}
          </Box>
          <Menu
            anchorEl={anchor}
            open={!!anchor}
            onClose={closeFav}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ style: { maxHeight: 360, width: 320 } }}
            // Não deixar o Menu roubar o foco à caixa de pesquisa nem fazer
            // "type-ahead" com as teclas que se escrevem na pesquisa.
            autoFocus={false}
            variant="menu"
          >
            {/* Caixa de pesquisa — só quando há favoritos que cheguem para
                justificar. Fixa no topo, não faz scroll com a lista. */}
            {showFavSearch && (
              <Box
                px={1.5} py={1}
                onKeyDown={e => e.stopPropagation()}
                style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1, display: 'flex', gap: 6, alignItems: 'center' }}
              >
                <TextField
                  autoFocus
                  size="small"
                  variant="outlined"
                  style={{ flex: 1 }}
                  placeholder="Procurar favorito…"
                  value={favQuery}
                  onChange={e => setFavQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ fontSize: 16, color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  size="small"
                  onClick={() => setFavDir(d => (d === 'asc' ? 'desc' : 'asc'))}
                  title={favDir === 'asc' ? 'A → Z' : 'Z → A'}
                  style={{ minWidth: 0, padding: '4px 8px', fontSize: '0.66rem', fontWeight: 800, color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 8, background: '#f0f9ff', textTransform: 'none', flexShrink: 0 }}
                >
                  {favDir === 'asc' ? 'A→Z' : 'Z→A'}
                </Button>
              </Box>
            )}

            {filteredFavorites.map(f => (
              <MenuItem
                key={f.id}
                onClick={() => { onPickFavorite(f); closeFav(); }}
                style={{ fontSize: '0.82rem' }}
              >
                <StarIcon style={{ fontSize: 14, color: '#f59e0b', marginRight: 8 }} />
                <Box>
                  <div style={{ fontWeight: 600 }}>{f.name}</div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{f.address}</div>
                </Box>
              </MenuItem>
            ))}

            {showFavSearch && filteredFavorites.length === 0 && (
              <Box px={2} py={1.5} style={{ fontSize: '0.76rem', color: '#94a3b8', fontStyle: 'italic' }}>
                Sem favoritos com “{favQuery}”.
              </Box>
            )}
          </Menu>

          <TextField
            size="small" variant="outlined" fullWidth
            label="Nome do local *"
            value={loc.locationName || ''}
            onChange={e => onChangeName(e.target.value)}
            style={{ marginBottom: 10 }}
          />

          <AddressFieldShared
            label="Morada *"
            value={loc.address || ''}
            onChange={onChangeAddress}
            onSelect={onAddressSelect}
            country={country}
            helperText="Escreve 3+ letras para sugestões com GPS"
          />
        </>
      )}

      {loc.latitude && loc.longitude && (
        <Box className={classes.coordChip}>
          <RoomIcon style={{ fontSize: 14, color: '#16a34a' }} />
          <span>
            GPS: {Number(loc.latitude).toFixed(5)}, {Number(loc.longitude).toFixed(5)}
          </span>
        </Box>
      )}
    </Box>
  );
}

export default FuelOrderWizard;
